import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Tooltip,
  Avatar,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { mockSystemUsers, SystemUser, getRoleLabel, getRoleColor } from '../services/mockUsers';
import { Role } from '../services/permissions';

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const [users, setUsers] = useState<SystemUser[]>(mockSystemUsers);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<SystemUser | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'agent_terrain' as Role,
    organization: '',
    isOnline: false,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = () => {
    // En production, charger depuis l'API
    setUsers(mockSystemUsers);
  };

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'agent_terrain',
      organization: '',
      isOnline: false,
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (userToEdit: SystemUser) => {
    setEditingUser(userToEdit);
    setFormData({
      name: userToEdit.name,
      email: userToEdit.email,
      role: userToEdit.role,
      organization: userToEdit.organization || '',
      isOnline: userToEdit.isOnline || false,
    });
    setOpenDialog(true);
  };

  const handleSave = () => {
    if (!formData.name || !formData.email) {
      return;
    }

    if (editingUser) {
      // Mise à jour
      const updatedUsers = users.map(u =>
        u.id === editingUser.id
          ? {
              ...u,
              name: formData.name,
              email: formData.email,
              role: formData.role,
              organization: formData.organization,
              isOnline: formData.isOnline,
            }
          : u
      );
      setUsers(updatedUsers);
    } else {
      // Création
      const newUser: SystemUser = {
        id: 'user-' + Date.now(),
        name: formData.name,
        email: formData.email,
        role: formData.role,
        organization: formData.organization,
        isOnline: formData.isOnline,
      };
      setUsers([...users, newUser]);
    }

    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleDelete = (userId: string) => {
    if (userId === user?.id) {
      alert('Vous ne pouvez pas supprimer votre propre compte.');
      return;
    }
    setUsers(users.filter(u => u.id !== userId));
    setOpenDeleteDialog(null);
  };

  const getRoleCounts = () => {
    return {
      agent_terrain: users.filter(u => u.role === 'agent_terrain').length,
      agence: users.filter(u => u.role === 'agence').length,
      soebra: users.filter(u => u.role === 'soebra').length,
      admin: users.filter(u => u.role === 'admin').length,
    };
  };

  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="error">
          Accès refusé. Seuls les administrateurs peuvent gérer les utilisateurs.
        </Alert>
      </Box>
    );
  }

  const roleCounts = getRoleCounts();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Gestion des Utilisateurs</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Nouvel utilisateur
        </Button>
      </Box>

      {/* Statistiques par rôle */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
          <Typography variant="body2" color="text.secondary">
            Agents Terrain
          </Typography>
          <Typography variant="h4" color="primary">
            {roleCounts.agent_terrain}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
          <Typography variant="body2" color="text.secondary">
            Agence
          </Typography>
          <Typography variant="h4" color="success.main">
            {roleCounts.agence}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
          <Typography variant="body2" color="text.secondary">
            SOBEBRA
          </Typography>
          <Typography variant="h4" color="warning.main">
            {roleCounts.soebra}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
          <Typography variant="body2" color="text.secondary">
            Administrateurs
          </Typography>
          <Typography variant="h4" color="error">
            {roleCounts.admin}
          </Typography>
        </Paper>
      </Box>

      {/* Liste des utilisateurs */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rôle</TableCell>
              <TableCell>Organisation</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((systemUser) => (
              <TableRow key={systemUser.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar>
                      {systemUser.name.charAt(0)}
                    </Avatar>
                    <Typography variant="body1" fontWeight="medium">
                      {systemUser.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{systemUser.email}</TableCell>
                <TableCell>
                  <Chip
                    label={getRoleLabel(systemUser.role)}
                    size="small"
                    sx={{
                      bgcolor: getRoleColor(systemUser.role),
                      color: 'white',
                    }}
                  />
                </TableCell>
                <TableCell>{systemUser.organization || '-'}</TableCell>
                <TableCell>
                  {systemUser.isOnline ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="En ligne"
                      size="small"
                      color="success"
                    />
                  ) : (
                    <Chip label="Hors ligne" size="small" color="default" />
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Modifier">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(systemUser)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setOpenDeleteDialog(systemUser.id)}
                      disabled={systemUser.id === user?.id}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Création/Modification */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nom"
            margin="normal"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Email"
            margin="normal"
            required
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Rôle</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as Role })}
            >
              <MenuItem value="agent_terrain">Agent Terrain</MenuItem>
              <MenuItem value="agence">Agence</MenuItem>
              <MenuItem value="soebra">SOBEBRA</MenuItem>
              <MenuItem value="admin">Administrateur</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Organisation"
            margin="normal"
            value={formData.organization}
            onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isOnline}
                onChange={(e) => setFormData({ ...formData, isOnline: e.target.checked })}
              />
            }
            label="En ligne"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.name || !formData.email}>
            {editingUser ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Suppression */}
      <Dialog open={openDeleteDialog !== null} onClose={() => setOpenDeleteDialog(null)}>
        <DialogTitle>Supprimer l'utilisateur</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(null)}>Annuler</Button>
          <Button
            onClick={() => openDeleteDialog && handleDelete(openDeleteDialog)}
            variant="contained"
            color="error"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;

